/**
 * The application frame: navigation, the outstanding-work counters, and the theme
 * toggle.
 *
 * The counters in the sidebar are the reason the dashboard read model exists. A review
 * queue an analyst has to navigate into before learning it is empty gets checked less
 * often, and the human gate only works if people look at it.
 */

import { useEffect, useState, type ReactElement } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { request } from "@/lib/api";
import type { TenantCounts } from "@/lib/schemas";
import { Badge, Button } from "@/components/ui";
import { applyTheme, initialTheme, type Theme } from "@/app/theme";
import { useSessionContext } from "@/features/auth/session";

const NAV = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/cases", label: "Cases" },
  { to: "/search", label: "Search" },
  { to: "/people", label: "People research" },
  { to: "/review", label: "Review" },
  { to: "/plugins", label: "Sources" },
  { to: "/admin", label: "Admin" },
  { to: "/api-keys", label: "API keys" },
  { to: "/mfa", label: "Security" },
  { to: "/settings", label: "Settings" },
  { to: "/audit", label: "Audit" },
  { to: "/compliance", label: "Compliance" },
];

/** The signed-in application frame. */
export function AppShell(): ReactElement {
  const [theme, setTheme] = useState<Theme>(() => initialTheme());
  const { session, signOut } = useSessionContext();

  useEffect(() => applyTheme(theme), [theme]);

  const counts = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => request<TenantCounts>("/dashboard"),
    refetchInterval: 60_000,
  });

  const outstanding =
    (counts.data?.pending_proposals ?? 0) +
    (counts.data?.unconfirmed_hypotheses ?? 0);

  return (
    <div className="flex min-h-screen">
      <nav
        aria-label="Main"
        className="flex w-56 shrink-0 flex-col border-r border-line bg-raised"
      >
        <div className="border-b border-line px-4 py-3">
          <p className="text-sm font-semibold">People Research</p>
          <p className="text-xs text-muted">{session?.roles.join(", ")}</p>
        </div>
        <ul className="flex-1 space-y-0.5 p-2">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded px-3 py-1.5 text-sm ${
                    isActive
                      ? "bg-accent/10 text-accent"
                      : "text-muted hover:text-ink"
                  }`
                }
              >
                <span>{item.label}</span>
                {item.label === "Review" && outstanding > 0 && (
                  <Badge tone="warn">{outstanding}</Badge>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="space-y-1 border-t border-line p-2">
          <Button
            tone="ghost"
            className="w-full justify-start"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? "☀ Light" : "☾ Dark"}
          </Button>
          <Button
            tone="ghost"
            className="w-full justify-start"
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        </div>
      </nav>
      <main className="min-w-0 flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
